/**
 * The board as a student sees it: the school's aircraft and instructors, with
 * only their own flights drawn on it. The scope is enforced on the server, so
 * this component simply renders what it is given.
 */
import { useState } from 'react';
import { useQuery } from '../api.js';
import { Drawer, ErrorNote, FindingList, Spinner } from '../ui.js';
import { LessonSheet } from './LessonSheet.js';
import { ResourceGrid, longDate, shiftDate, type DayBoard } from './ResourceGrid.js';

export function PortalBoard({ studentId }: { studentId: number }): JSX.Element {
  const [date, setDate] = useState('');
  const { data, error, loading } = useQuery<DayBoard & { ownOnly: boolean }>(
    `/schedule/day${date ? `?date=${date}` : ''}`,
  );

  // A block names a reservation; what the student wants from it is the lesson.
  const [openReservation, setOpenReservation] = useState<number | null>(null);
  const picked = data?.reservations.find((r) => r.id === openReservation);

  if (error) return <ErrorNote message={error} />;

  const effectiveDate = date || data?.date || '';
  const mine = data?.reservations ?? [];

  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className="row" style={{ justifyContent: 'space-between', gap: 12 }}>
        <span style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.014em' }}>
          {effectiveDate ? longDate(effectiveDate) : 'Today'}
        </span>
        <span className="row" style={{ gap: 8 }}>
          <button className="btn btn--sm" onClick={() => setDate(shiftDate(effectiveDate, -1))}>‹ Previous</button>
          <button className="btn btn--sm" onClick={() => setDate('')}>Today</button>
          <button className="btn btn--sm" onClick={() => setDate(shiftDate(effectiveDate, 1))}>Next ›</button>
        </span>
      </div>

      {loading && !data && <Spinner />}

      {data && (
        <>
          <ResourceGrid board={data} onOpen={setOpenReservation} ownOnly />

          {mine.length === 0 && (
            <div className="card">
              <div className="empty">
                Nothing of yours on {effectiveDate ? longDate(effectiveDate) : 'this day'}.
                The rows show the school's aircraft and instructors; only your own flights appear on them.
              </div>
            </div>
          )}

          {data.alerts.length > 0 && (
            <div className="card">
              <div className="card__head" style={{ display: 'block' }}>
                <span className="card__title">Needs review · {data.alerts.length}</span>
                <span className="card__meta" style={{ display: 'block', marginTop: 2 }}>
                  Advisory only. Your instructor sees these too — the pilot in command decides.
                </span>
              </div>
              <div className="findings--grid"><FindingList findings={data.alerts} /></div>
            </div>
          )}
        </>
      )}

      {picked?.lessonId && (
        <LessonSheet
          studentId={studentId}
          lessonId={picked.lessonId}
          onClose={() => setOpenReservation(null)}
        />
      )}

      {/* A session that is not a syllabus lesson has no lesson to open. */}
      {picked && !picked.lessonId && (
        <Drawer
          narrow
          title={picked.activityLabel || picked.kind}
          subtitle={`${new Date(picked.startsAt).toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })} · ${clock(picked.startsAt)}–${clock(picked.endsAt)}`}
          onClose={() => setOpenReservation(null)}
        >
          <div className="kv"><span>Aircraft</span><span>{picked.aircraftTail}</span></div>
          <div className="kv"><span>Instructor</span><span>{picked.instructorName || '—'}</span></div>
          <div className="kv"><span>Status</span><span>{picked.status}</span></div>
          {picked.notes && <div className="kv"><span>Notes</span><span style={{ fontWeight: 400 }}>{picked.notes}</span></div>}
        </Drawer>
      )}
    </div>
  );
}

function clock(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}
